import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './Announcements.module.scss';
import Loader from '../../Molecules/Loader/Loader';
import Buttons from '../../Atoms/Buttons/Buttons';
import DeletePopup from '../../Molecules/Custom/DeletePopup';
import SearchInput from '../../Atoms/Inputs/SearchInput';
import TablePagination from '../../Molecules/Pagination/TablePagination';
import { useToast } from '../../Molecules/Custom/CustomToaster/Toast';
import {
  useFetchAnnouncements,
  useFetchActiveAnnouncement,
  useSetAnnouncementPublished,
  useDeleteAnnouncement,
  useResetAnnouncementDismissals,
  useCreatePreviewToken,
} from '../../../Hooks/Announcement';
import { WEBSITE_URL, isLocalOrigin } from '../../../Constants';

/**
 * الإعلانات — the list screen for the website's announcement pop-up.
 *
 * Requirement A1 of the brief: create, edit, enable, disable, publish and
 * unpublish without developer intervention. Plan:
 * DPA-FE `docs/Announcements&visitors/01-development-roadmap.md`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE STATUS CHIP IS THE SERVER'S, NOT OURS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Draft / Scheduled / Live / Expired comes from `row.status` and is NEVER
 * re-derived here from `isPublished` plus the window. The website asks the
 * server what is live, against the server's clock; if this screen computed the
 * same thing against the operator's browser clock the two would disagree, and
 * the CMS would cheerfully show «مباشر» for something no visitor can see. One
 * server-computed field removes that entire class of bug — which is why §A2 of
 * the backend request asks for it explicitly.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THERE IS A "CURRENTLY LIVE" STRIP ABOVE THE TABLE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Several announcements can be `Live` at once; the website shows exactly ONE,
 * resolved by `priority` (higher wins). A table of four green chips does not
 * tell an operator which one a visitor is actually looking at. The strip calls
 * the same anonymous `Active` endpoint the public site calls, so it answers
 * that question with the website's own answer rather than a re-implementation
 * of its tie-break rule.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * «إظهاره من جديد» — the least obvious button on the screen
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Editing an announcement's CONTENT already re-shows it to everyone who
 * dismissed it: the backend bumps `version` and the browser's dismissal memory
 * is keyed on it. This button is for the other case — a correct, unchanged
 * announcement that should be seen again, like an exam reminder dismissed last
 * week. Without it the only way to force a re-display is a cosmetic edit, which
 * is a bad habit to teach an operator.
 */

const STATUS_FILTERS = [
  { value: '', label: 'الكل' },
  { value: 'Live', label: 'مباشر' },
  { value: 'Scheduled', label: 'مجدول' },
  { value: 'Draft', label: 'مسودة' },
  { value: 'Expired', label: 'منتهٍ' },
];

const STATUS_LABEL = {
  Draft: 'مسودة',
  Scheduled: 'مجدول',
  Live: 'مباشر',
  Expired: 'منتهٍ',
};

const STATUS_CLASS = {
  Draft: 'chipDraft',
  Scheduled: 'chipScheduled',
  Live: 'chipLive',
  Expired: 'chipExpired',
};

const FREQUENCY_LABEL = {
  EveryVisit: 'في كل زيارة',
  OncePerSession: 'مرة واحدة لكل جلسة',
  OncePerDevice: 'مرة واحدة لكل جهاز',
  EveryNDays: 'كل عدة أيام',
};

const LAYOUT_LABEL = {
  TextOnly: 'نص فقط',
  ImageOnly: 'صورة فقط',
  ImageAndText: 'صورة ونص',
};

/**
 * Dates are stored and sent as UTC. An operator thinks in Dubai time, so they
 * are rendered in the browser's own zone — `toLocaleString` with no explicit
 * zone does exactly that, which is correct for a CMS used from one city.
 *
 * `—` for an absent `endsOn` is meaningful on this screen: open-ended, never
 * expires. It is not a missing value.
 */
const formatInstant = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('ar-AE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const PAGE_SIZE = 10;

const AnnouncementsPage = () => {
  const navigate = useNavigate();
  const customizedToast = useToast();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  /** Which row has a write in flight, so only that row's controls are disabled. */
  const [busyId, setBusyId] = useState(null);

  const { data, isLoading, isError, error, refetch } = useFetchAnnouncements({
    page,
    pageSize: PAGE_SIZE,
    searchText: search,
    status,
  });

  const { data: active, refetch: refetchActive } = useFetchActiveAnnouncement();

  const { mutate: setPublished } = useSetAnnouncementPublished();
  const { mutate: removeAnnouncement, isLoading: isDeleting } =
    useDeleteAnnouncement();
  const { mutate: resetDismissals } = useResetAnnouncementDismissals();
  const { mutate: mintPreviewToken } = useCreatePreviewToken();

  const rows = useMemo(() => data?.items ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  /* Both lists move together after any write: the table shows what exists, the
   * strip shows which one a visitor sees, and a publish can change the second
   * without changing the first. */
  const refreshAll = () => {
    refetch();
    refetchActive();
  };

  const handleTogglePublished = (row) => {
    setBusyId(row.id);
    setPublished(
      { id: row.id, isPublished: !row.isPublished },
      {
        onSuccess: (result) => {
          setBusyId(null);
          /* The server recomputes `status`, and it is not always the obvious
             one: publishing a record whose window has not opened yet makes it
             «مجدول», not «مباشر». Saying which it became is the difference
             between an operator trusting this screen and going to check the
             website. */
          const nextStatus = STATUS_LABEL[result?.status] ?? '';
          customizedToast.open(
            row.isPublished
              ? 'تم إلغاء نشر الإعلان'
              : `تم نشر الإعلان${nextStatus ? ` — الحالة الآن: ${nextStatus}` : ''}`
          );
          refreshAll();
        },
        onError: (err) => {
          setBusyId(null);
          /* A 409 here is a real refusal with a reason the operator can act on
             — publishing a record with no content, for instance — so the
             server's own sentence is shown rather than a generic failure. */
          toast.error(err?.message ?? 'تعذّر تغيير حالة النشر');
        },
      }
    );
  };

  const handleResetDismissals = (row) => {
    setBusyId(row.id);
    resetDismissals(row.id, {
      onSuccess: () => {
        setBusyId(null);
        customizedToast.open('سيظهر هذا الإعلان من جديد لجميع الزوار');
        refreshAll();
      },
      onError: (err) => {
        setBusyId(null);
        toast.error(err?.message ?? 'تعذّر إعادة إظهار الإعلان');
      },
    });
  };

  /**
   * Preview opens the WEBSITE, not a drawing of it.
   *
   * The window is opened BEFORE the request and navigated afterwards. Opening
   * it inside the `onSuccess` callback would be a pop-up blocked by every
   * browser, because by then the click is no longer the cause. This is the
   * standard shape for "open a tab at the end of an async call", and the
   * blocked-window case is reported rather than failing silently.
   */
  const handlePreview = (row) => {
    if (!WEBSITE_URL) {
      toast.error(
        'عنوان الموقع غير مُعرَّف. حدّد «websiteUrl» في ملف env-config.js على الخادم.'
      );
      return;
    }

    /**
     * The website origin says `localhost` while the CMS itself is not running
     * on one — so the build carries a developer's value and nothing on this
     * server has overridden it.
     *
     * Caught BEFORE opening anything. The previous behaviour was to open the
     * tab anyway, and on a deployed server `localhost` resolves to THAT SERVER,
     * so the operator was handed a tab showing something unrelated — or a
     * connection error — with no indication of why. An unreachable preview that
     * explains itself is worth far more than one that fails quietly.
     */
    if (isLocalOrigin(WEBSITE_URL) && !isLocalOrigin(window.location.origin)) {
      toast.error(
        `إعداد عنوان الموقع غير صحيح لهذه البيئة (${WEBSITE_URL}). ` +
          'حدّد «websiteUrl» في ملف env-config.js على الخادم ثم أعد تحميل الصفحة.'
      );
      return;
    }

    /**
     * ⚠ NO `noopener` IN THE FEATURES STRING. `window.open(url, name, 'noopener')`
     * RETURNS NULL BY SPECIFICATION — the whole point of the flag is that the
     * caller is given no handle to the new window. An earlier version passed it
     * and then treated the null as "the pop-up was blocked", so the preview
     * button reported a blocked pop-up EVERY TIME, in every browser, and never
     * opened anything. The blank tab it had just opened was left behind.
     *
     * The handle is required here: the URL is not known until `PreviewToken`
     * answers, and opening the tab only after that async call would lose the
     * user gesture and be blocked for real.
     *
     * `opener` is severed manually instead. The target is our own website, so
     * there is nothing to protect against — this is hygiene, not a fix.
     */
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('تم حجب النافذة المنبثقة. اسمح بالنوافذ المنبثقة ثم أعد المحاولة.');
      return;
    }
    try {
      tab.opener = null;
    } catch {
      /* Some browsers refuse the assignment; harmless either way. */
    }

    setBusyId(row.id);
    mintPreviewToken(row.id, {
      onSuccess: (result) => {
        setBusyId(null);
        const token = result?.token;
        if (!token) {
          tab.close();
          toast.error('تعذّر إنشاء رابط المعاينة');
          return;
        }
        /* `WEBSITE_URL` is normalised without a trailing slash in Constants.js,
         * but a stray one in .env would produce `//preview/...` - a
         * protocol-relative path to a host called "preview". Trimmed here. */
        tab.location.href = `${WEBSITE_URL.replace(/\/+$/, '')}/preview/announcement?token=${encodeURIComponent(
          token
        )}`;
      },
      onError: (err) => {
        setBusyId(null);
        tab.close();
        toast.error(err?.message ?? 'تعذّر إنشاء رابط المعاينة');
      },
    });
  };

  const handleDelete = () => {
    const id = pendingDelete?.id;
    if (!id) return;

    removeAnnouncement(id, {
      onSuccess: () => {
        customizedToast.open('تم حذف الإعلان بنجاح');
        setPendingDelete(null);
        /* The deleted row may have been the one on screen for visitors, so the
           live strip is refreshed too — the next-highest-priority announcement
           takes over immediately (§A7). */
        refreshAll();
      },
      onError: (err) => {
        toast.error(err?.message ?? 'تعذّر حذف الإعلان');
        setPendingDelete(null);
      },
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.heading}>
          <h2 className={styles.title}>الإعلانات</h2>
          <p className={styles.hint}>
            نافذة منبثقة تظهر لزوار الموقع. يظهر إعلان واحد فقط في كل مرة؛ عند
            وجود أكثر من إعلان مباشر تُعرَض الأولوية الأعلى.
          </p>
        </div>

        <Buttons
          text='إنشاء إعلان'
          rounded={true}
          color='rgba(13, 160, 110, 1)'
          // `type='submit'` only to get the atom's BARE button — its other
          // branch wraps the button in a react-router `<Link to={undefined}>`.
          type='submit'
          width={160}
          // `.aud-button-rounded` carries `flex: 1`, which in this flex header
          // would stretch the button regardless of `width`.
          style={{ flex: '0 0 auto' }}
          handleOnClick={() => navigate('/home/add-announcement')}
        />
      </div>

      {/* What a visitor is actually seeing right now — the same anonymous
          endpoint the public website calls, so it cannot disagree with it. */}
      <div
        className={`${styles.liveStrip} ${
          active?.id ? styles.liveStripOn : styles.liveStripOff
        }`}
        role="status"
      >
        {active?.id ? (
          <>
            <strong className={styles.liveStripLabel}>يُعرض الآن للزوار:</strong>{' '}
            <span>{active.title || LAYOUT_LABEL[active.layout] || 'إعلان بصورة'}</span>
          </>
        ) : (
          <span>لا يوجد إعلان معروض للزوار حاليًا.</span>
        )}
      </div>

      <div className={styles.filters}>
        <SearchInput search={search} setSearch={setSearch} />

        <div className={styles.statusFilter} role="group" aria-label="تصفية حسب الحالة">
          {STATUS_FILTERS.map((item) => (
            <button
              key={item.value || 'all'}
              type="button"
              className={`${styles.filterChip} ${
                status === item.value ? styles.filterChipActive : ''
              }`}
              // Any filter change returns to page 1: staying on page 3 of a
              // narrower result set asks the API for a page that does not
              // exist and shows an empty table.
              onClick={() => {
                setStatus(item.value);
                setPage(1);
              }}
              aria-pressed={status === item.value}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <p className={styles.empty}>
            {error?.message ?? 'تعذّر تحميل الإعلانات. يرجى المحاولة مرة أخرى.'}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {search || status
              ? 'لا توجد إعلانات مطابقة لعوامل التصفية.'
              : 'لا توجد إعلانات بعد. أنشئ إعلانًا ليظهر لزوار الموقع.'}
          </p>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">العنوان</th>
                  <th scope="col">الحالة</th>
                  <th scope="col">يبدأ في</th>
                  <th scope="col">ينتهي في</th>
                  <th scope="col">التكرار</th>
                  <th scope="col">الأولوية</th>
                  <th scope="col">النشر</th>
                  <th scope="col">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isBusy = busyId === row.id;
                  const isLiveNow = active?.id === row.id;

                  return (
                    <tr key={row.id} className={isLiveNow ? styles.rowLive : undefined}>
                      <td className={styles.titleCell}>
                        {row.title || (
                          <span className={styles.muted}>
                            {LAYOUT_LABEL[row.layout] ?? 'بدون عنوان'}
                          </span>
                        )}
                        {isLiveNow && (
                          <span className={styles.nowBadge}>يُعرض الآن</span>
                        )}
                      </td>
                      <td>
                        {/* Text, not colour alone — WCAG 1.4.1. */}
                        <span
                          className={`${styles.chip} ${
                            styles[STATUS_CLASS[row.status]] ?? ''
                          }`}
                        >
                          {STATUS_LABEL[row.status] ?? row.status ?? '—'}
                        </span>
                      </td>
                      <td className={styles.dateCell}>{formatInstant(row.startsOn)}</td>
                      <td className={styles.dateCell}>
                        {row.endsOn ? (
                          formatInstant(row.endsOn)
                        ) : (
                          <span className={styles.muted}>بلا نهاية</span>
                        )}
                      </td>
                      <td>{FREQUENCY_LABEL[row.displayFrequency] ?? '—'}</td>
                      <td className={styles.numberCell}>{row.priority ?? 0}</td>
                      <td>
                        <div className={styles.switchCell}>
                          <input
                            id={`ann-pub-${row.id}`}
                            type="checkbox"
                            className={styles.switch}
                            checked={!!row.isPublished}
                            disabled={isBusy}
                            onChange={() => handleTogglePublished(row)}
                            aria-label={`نشر الإعلان ${row.title || ''}`}
                          />
                          {isBusy && <span className={styles.switchHint}>جارٍ الحفظ…</span>}
                        </div>
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => navigate(`/home/edit-announcement/${row.id}`)}
                          >
                            تعديل
                          </button>
                          <button
                            type="button"
                            className={styles.linkButton}
                            disabled={isBusy}
                            onClick={() => handlePreview(row)}
                          >
                            معاينة
                          </button>
                          <button
                            type="button"
                            className={styles.linkButton}
                            disabled={isBusy}
                            onClick={() => handleResetDismissals(row)}
                            title="يعيد إظهار الإعلان لكل زائر أغلقه سابقًا، دون تعديل محتواه."
                          >
                            إظهاره من جديد
                          </button>
                          <button
                            type="button"
                            className={`${styles.linkButton} ${styles.danger}`}
                            onClick={() => setPendingDelete(row)}
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <TablePagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          itemsPerPage={PAGE_SIZE}
        />
      )}

      {pendingDelete && (
        <DeletePopup
          title='الإعلان'
          isLoading={isDeleting}
          description={`${
            pendingDelete.title || 'إعلان بدون عنوان'
          } — الحذف نهائي. إذا كنت تنوي استخدامه لاحقًا، ألغِ نشره بدل حذفه.`}
          handleOnClose={() => setPendingDelete(null)}
          handleDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default AnnouncementsPage;
